/**
 * Email template editor — two modes, one draft, and the frame it sits in.
 *
 * Open one template and edit it either visually with the self-hosted GrapesJS drag-and-drop
 * builder (the "Design" tab) or straight in HTML (the "HTML" tab); the two share a single draft
 * so an operator can move between them.
 *
 * ## What a template holds now
 *
 * A **body**, not a document. The header, the card and the footer come from the email layout, so
 * changing the footer is one edit rather than twelve, and the one somebody forgets is not a
 * customer reading last year's company address. `usesLayout` says which of the two a given row
 * holds: every shipped template is a body, and a hand-built whole document is still allowed for
 * a row that predates the layout.
 *
 * That is why the preview wraps the draft in the layout before rendering it. A preview of the
 * bare fragment previews something no customer will ever receive.
 *
 * ## The preheader
 *
 * Its own field, because the alternative is what every inbox shows when nobody sets one: the
 * first words of the body, which for a receipt is the same sentence under every subject line.
 * It is the second-most-read text in an email and the least often written.
 *
 * "Save" upserts the template, "Restore" puts it back to the shipped default (recoverably), and
 * "Send test" delivers a live copy to one address. Superadmin only.
 */
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { useBrand } from '../branding/BrandProvider.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  EmailImage,
  EmailLayout,
  EmailTemplate,
  PlatformSettings,
  RenderPreviewResponse,
} from '../contracts/types.js';
import { EMAIL_GLOBAL_VARIABLES } from '../contracts/vocab.js';
import { Badge, Button, Card, ErrorBox, Field, Input, LoadingState, Switch } from '../components/ui.js';
import type { EmailBuilderHandle } from '../components/EmailBuilder.js';
import { ConfirmModal } from '../components/Modal.js';
import { EmailImageLibrary } from '../components/EmailImageLibrary.js';
import { EmailPreview } from '../components/EmailPreview.js';
import { IconArrowLeft, IconBrush, IconCode, IconRefresh, IconSend } from '../components/icons.js';

/**
 * The GrapesJS builder (and its ~1 MB of editor code) is code-split into its own chunk and only
 * fetched when the Design tab first mounts. The rest of the console never pays for it.
 */
const EmailBuilder = lazy(() =>
  import('../components/EmailBuilder.js').then((m) => ({ default: m.EmailBuilder })),
);
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { referencedImages } from '../lib/emailAttachments.js';
import { defaultEmailLayoutContent, emailTokens, renderWithLayout } from '../lib/emailLayout.js';
import { extractTokens, renderPreview, SAMPLE_VARS } from '../lib/emailVars.js';

type Mode = 'design' | 'html';

/** Tokens the server fills, which a template must therefore not declare as its own variables. */
const GLOBALS = new Set<string>(EMAIL_GLOBAL_VARIABLES);

const HTML_MAX = 200_000;
const PREHEADER_MAX = 200;

interface Loaded {
  readonly template: EmailTemplate | null;
  readonly settings: PlatformSettings;
  readonly layout: EmailLayout | null;
  readonly images: readonly EmailImage[];
}

export function EmailTemplateEditorPage(): JSX.Element {
  const { key = '' } = useParams<{ key: string }>();
  const { api } = useAuth();

  const load = useCallback(async (): Promise<Loaded> => {
    const [list, settings, layout, images] = await Promise.all([
      api.listEmailTemplates(),
      api.getBusinessSettings(),
      api.getEmailLayout(),
      api.listEmailImages(),
    ]);
    return {
      template: list.templates.find((t) => t.key === key) ?? null,
      settings,
      layout,
      images: images.images,
    };
  }, [api, key]);

  const state = useAsync(load, [load]);

  return (
    <>
      <div className="page-head">
        <Link to="/email-templates" className="backlink">
          <IconArrowLeft size={16} /> Back to templates
        </Link>
      </div>

      {state.loading ? (
        <LoadingState label="Loading template…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : state.data === null || state.data.template === null ? (
        <ErrorBox message="That template does not exist." />
      ) : (
        <Editor
          // Remount on a template change so the builder boots on the right document.
          key={state.data.template.key}
          template={state.data.template}
          settings={state.data.settings}
          layout={state.data.layout}
          images={state.data.images}
          onImages={(images) => state.setData((prev) => (prev === null ? prev : { ...prev, images }))}
        />
      )}
    </>
  );
}

function Editor({
  template,
  settings,
  layout: storedLayout,
  images,
  onImages,
}: {
  template: EmailTemplate;
  settings: PlatformSettings;
  layout: EmailLayout | null;
  images: readonly EmailImage[];
  onImages: (next: readonly EmailImage[]) => void;
}): JSX.Element {
  const { api, session } = useAuth();
  const brand = useBrand();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'admins:write');
  const layout = storedLayout?.content ?? defaultEmailLayoutContent();

  const [name, setName] = useState(template.name);
  const [subject, setSubject] = useState(template.subject);
  const [preheader, setPreheader] = useState(template.preheader);
  const [enabled, setEnabled] = useState(template.enabled);
  const [usesLayout, setUsesLayout] = useState(template.usesLayout);
  const [html, setHtml] = useState(template.html);
  const [mode, setMode] = useState<Mode>('design');
  const [previewHtml, setPreviewHtml] = useState(template.html);
  const [server, setServer] = useState<RenderPreviewResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const builderRef = useRef<EmailBuilderHandle | null>(null);

  const tokens = useMemo(
    () => ({ ...SAMPLE_VARS, ...emailTokens(settings, layout, brand.logoUrl) }),
    [settings, layout, brand.logoUrl],
  );

  // Pull the freshest draft: from the builder when the Design tab is active, else from state.
  const currentHtml = useCallback((): string => {
    if (mode === 'design' && builderRef.current !== null) return builderRef.current.getHtml();
    return html;
  }, [mode, html]);

  /**
   * The preview refreshes explicitly rather than on every keystroke.
   *
   * In the HTML tab a keystroke-driven preview re-parses and re-lays-out the whole document on
   * every character, which on a table-based email is visibly laggy. In the Design tab it is not
   * even possible: the builder owns the document and hands it over only when asked.
   */
  const refreshPreview = useCallback(() => {
    setPreviewHtml(currentHtml());
    setServer(null);
  }, [currentHtml]);

  /**
   * The rendered document.
   *
   * The server's own render wins when there is one, because that is what actually gets sent.
   * Otherwise the draft is wrapped in the layout here, which is the only way to preview an edit
   * that has not been saved: a template stores a body, and the chrome lives elsewhere.
   */
  const localDocument = useMemo(() => {
    const bodyHtml = renderPreview(previewHtml, tokens);
    if (!usesLayout) return bodyHtml;
    return renderWithLayout(
      layout,
      {
        platformName: settings.platformName,
        logoUrl: brand.logoUrl,
        supportEmail: settings.supportEmail,
        websiteUrl: settings.websiteUrl,
      },
      { preheader, bodyHtml },
    );
  }, [previewHtml, tokens, usesLayout, layout, settings, brand.logoUrl, preheader]);

  const documentHtml = server?.html ?? localDocument;
  const renderedSubject = server?.subject ?? renderPreview(subject, tokens);
  const used = useMemo(() => referencedImages(previewHtml, images), [previewHtml, images]);

  function switchMode(next: Mode) {
    if (next === mode) return;
    // Leaving Design: capture the builder's HTML into the shared draft and the preview, so the
    // two tabs never show different documents.
    if (mode === 'design' && builderRef.current !== null) {
      const draft = builderRef.current.getHtml();
      setHtml(draft);
      setPreviewHtml(draft);
      setServer(null);
    }
    setMode(next);
  }

  function insertImage(tag: string): void {
    const draft = currentHtml();
    const next = `${draft}\n${tag}`;
    setHtml(next);
    setPreviewHtml(next);
    setServer(null);
    if (mode === 'design') {
      // The builder holds its own copy and will not see this edit, so move the operator to the
      // tab where the change they just made is the one on screen.
      setMode('html');
      toast.info('Switched to HTML', 'The image went in at the end of the body.');
    } else {
      toast.info('Image inserted', 'It went in at the end. Move it if you want it elsewhere.');
    }
  }

  async function save(): Promise<EmailTemplate | null> {
    const draft = currentHtml();
    setHtml(draft);
    setPreviewHtml(draft);
    if (name.trim() === '' || subject.trim() === '' || draft.trim() === '') {
      toast.error('Something is missing', 'A template needs a name, a subject and some HTML.');
      return null;
    }
    if (draft.length > HTML_MAX) {
      toast.error(
        'This template is too long',
        `It is ${draft.length.toLocaleString()} characters and the limit is ${HTML_MAX.toLocaleString()}.`,
      );
      return null;
    }
    const variables = extractTokens(draft).filter((t) => !GLOBALS.has(t));
    setSaving(true);
    try {
      const next = await api.upsertEmailTemplate(template.key, {
        name: name.trim(),
        subject: subject.trim(),
        preheader: preheader.trim(),
        html: draft,
        // Always sent explicitly. Omitting it means "leave it alone" to the API, which is the
        // right default there, but this form has a switch for it and the switch must win.
        usesLayout,
        variables,
        enabled,
      });
      toast.success('Template saved');
      // Now that it is stored, the server can render it, and its render is the authority.
      try {
        setServer(await api.previewEmailTemplate(template.key, { sampleVars: SAMPLE_VARS }));
      } catch {
        // The save landed. A preview that did not is not worth an error over the top of it.
      }
      return next;
    } catch (err) {
      toast.error('Could not save', messageOf(err));
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function restore(): Promise<void> {
    setRestoring(true);
    try {
      const next = await api.restoreEmailTemplate(template.key);
      setName(next.name);
      setSubject(next.subject);
      setPreheader(next.preheader);
      setHtml(next.html);
      setPreviewHtml(next.html);
      setUsesLayout(next.usesLayout);
      setEnabled(next.enabled);
      setServer(null);
      setRestoreOpen(false);
      // The builder is holding the old document and does not watch its prop, so the raw tab is
      // the only one that would show the restored content. Say so rather than let it look broken.
      if (mode === 'design') setMode('html');
      toast.success('Template restored', 'The shipped default is back. Your previous version is in the history.');
    } catch (err) {
      toast.error('Could not restore', messageOf(err));
    } finally {
      setRestoring(false);
    }
  }

  async function sendTest() {
    const to = testTo.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      toast.error('Enter a valid email', 'Send the test to a real inbox you control.');
      return;
    }
    setTesting(true);
    try {
      // The server renders the stored template, so persist any edits first.
      const saved = await save();
      if (saved === null) return;
      await api.testSendEmailTemplate(template.key, { to, sampleVars: SAMPLE_VARS });
      toast.success('Test sent', `A sample ${template.name} email is on its way to ${to}.`);
    } catch (err) {
      toast.error('Could not send test', messageOf(err));
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="tpl-editor">
      <Card>
        <div className="tpl-editor__head">
          <div className="tpl-editor__title">
            <Field label="Template name" htmlFor="tpl-name">
              <Input id="tpl-name" value={name} disabled={!canWrite} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          <label className="tpl-editor__enabled">
            <span className="field__label">{enabled ? 'Enabled' : 'Disabled'}</span>
            <Switch checked={enabled} onChange={setEnabled} disabled={!canWrite} label="Template enabled" />
          </label>
        </div>

        <Field label="Subject line" htmlFor="tpl-subject">
          <Input
            id="tpl-subject"
            value={subject}
            disabled={!canWrite}
            placeholder="Your {{platformName}} receipt"
            onChange={(e) => setSubject(e.target.value)}
          />
        </Field>

        <Field label="Preheader" htmlFor="tpl-preheader">
          <Input
            id="tpl-preheader"
            value={preheader}
            maxLength={PREHEADER_MAX}
            disabled={!canWrite}
            placeholder="The grey line an inbox shows beside the subject"
            onChange={(e) => setPreheader(e.target.value)}
          />
          <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
            Leave it empty and inboxes show the first words of the message instead, which under
            every receipt is the same sentence.
          </div>
        </Field>

        <div className="setting-row">
          <span>
            <span className="field__label">Use the email layout</span>
            <span className="faint" style={{ fontSize: 12, display: 'block' }}>
              {usesLayout
                ? 'This template is a body. The header, card and footer come from your layout.'
                : 'This template is a whole document and draws its own frame. Nothing from the layout applies.'}
            </span>
          </span>
          <Switch
            checked={usesLayout}
            onChange={setUsesLayout}
            disabled={!canWrite}
            label="Use the email layout"
          />
        </div>

        <div className="tpl-vars">
          <span className="faint" style={{ fontSize: 12 }}>Always available:</span>
          {EMAIL_GLOBAL_VARIABLES.map((v) => (
            <code key={v} className="tpl-token tpl-token--global">{`{{${v}}}`}</code>
          ))}
          {template.variables.map((v) => (
            <code key={v} className="tpl-token">{`{{${v}}}`}</code>
          ))}
          <Badge tone={template.category === 'marketing' ? 'amber' : 'blue'}>
            {template.category === 'marketing' ? 'Marketing' : 'Transactional'}
          </Badge>
        </div>
      </Card>

      <div className="tpl-editor__grid">
        <Card
          flush
          title="Editor"
          actions={
            <div className="seg">
              <button className={`seg__btn${mode === 'design' ? ' seg__btn--on' : ''}`} onClick={() => switchMode('design')}>
                <IconBrush size={15} /> Design
              </button>
              <button className={`seg__btn${mode === 'html' ? ' seg__btn--on' : ''}`} onClick={() => switchMode('html')}>
                <IconCode size={15} /> HTML
              </button>
            </div>
          }
        >
          {mode === 'design' ? (
            <Suspense
              fallback={
                <div className="email-builder" style={{ display: 'grid', placeItems: 'center' }}>
                  <LoadingState label="Loading the builder…" />
                </div>
              }
            >
              <EmailBuilder ref={builderRef} initialHtml={html} />
            </Suspense>
          ) : (
            <textarea
              className="tpl-html"
              spellCheck={false}
              value={html}
              disabled={!canWrite}
              onChange={(e) => setHtml(e.target.value)}
            />
          )}
        </Card>

        <Card
          flush
          title="Preview"
          actions={
            <Button size="sm" variant="ghost" onClick={refreshPreview}>
              Refresh
            </Button>
          }
        >
          <EmailPreview
            subject={renderedSubject}
            preheader={server?.preheader ?? preheader}
            html={documentHtml}
            text={server?.text}
            senderName={layout.senderName === '' ? settings.platformName : layout.senderName}
            fromEmail={settings.supportEmail}
            images={used}
          />
          {server !== null && server.missingVariables.length > 0 && (
            <div className="mailnote" role="alert" style={{ margin: 12 }}>
              Nothing was supplied for {server.missingVariables.map((v) => `{{${v}}}`).join(', ')}.
              A customer would see the placeholder text.
            </div>
          )}
        </Card>
      </div>

      <Card>
        <div className="tpl-actions">
          <div className="tpl-actions__test">
            <Input
              type="email"
              value={testTo}
              placeholder="you@example.com"
              disabled={!canWrite}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <Button variant="ghost" loading={testing} disabled={!canWrite} onClick={() => void sendTest()}>
              <IconSend size={15} /> Send test
            </Button>
          </div>
          <div className="tpl-actions__spacer" />
          <Button variant="ghost" disabled={!canWrite} onClick={() => setRestoreOpen(true)}>
            <IconRefresh size={15} /> Restore the default
          </Button>
          <Button variant="primary" loading={saving} disabled={!canWrite} onClick={() => void save()}>
            Save template
          </Button>
        </div>
        {!canWrite && (
          <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>
            Your role can view but not change templates.
          </div>
        )}
      </Card>

      <EmailImageLibrary
        images={images}
        canWrite={canWrite}
        onChange={onImages}
        inUseIds={used.map((i) => i.id)}
        hint="Shared with every template and broadcast. Insert one to drop its tag into the body."
        onInsert={(_image, tag) => insertImage(tag)}
      />

      <ConfirmModal
        open={restoreOpen}
        title="Restore the shipped default"
        description="This replaces the current content with the one this template shipped with."
        confirmLabel="Restore"
        loading={restoring}
        onConfirm={() => void restore()}
        onClose={() => setRestoreOpen(false)}
      >
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Your current version is saved into the history first, so this can be undone. The
          subject, the preheader and the declared variables all go back to the default too.
        </p>
      </ConfirmModal>
    </div>
  );
}
